import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import MapaCuraResult from '@/components/MapaCuraResult'

const GOLD_SOFT = '#E0B870'

interface Registro {
  _id: string
  name: string
  email: string
  createdAt: string
  answered?: number
}

interface Relatorio {
  score: number
  sectionsFound: string[]
  triadsPerElement: Record<string, number>
  agradecoSentences: number
  agradecoPastTense: string[]
  agradecoNegative: string[]
  thirdPersonInTrindade: string[]
  dashesFound: number
  words: number
  notes: string[]
}

interface Resultado {
  name: string
  model: string
  gemini: string
  claude: string
  relatorioGemini: Relatorio | null
  relatorioClaude: Relatorio | null
  erroGemini?: string
  erroClaude?: string
}

interface RespostaModelo {
  name?: string
  provider?: string
  model?: string
  text?: string
  relatorio?: Relatorio | null
  error?: string
}

function ScoreCard({ titulo, rel, erro }: { titulo: string; rel: Relatorio | null; erro?: string }) {
  const score = rel?.score ?? 0
  const cor = score >= 85 ? '#7FB069' : score >= 65 ? GOLD_SOFT : '#E07B3C'
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: `${cor}55`, background: 'rgba(255,255,255,0.02)' }}>
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-body text-[13px] tracking-[0.2em] uppercase" style={{ color: 'var(--ink-soft,#a89f8c)' }}>
          {titulo}
        </p>
        <p className="font-display numerals text-2xl" style={{ color: cor }}>
          {rel ? score : '—'}
          <span className="text-sm">/100</span>
        </p>
      </div>
      {erro && <p className="font-body text-sm" style={{ color: '#E07B3C' }}>{erro}</p>}
      {rel && (
        <ul className="space-y-1.5 font-body text-[13px]" style={{ color: 'var(--ink-soft,#a89f8c)' }}>
          <li>
            Tríades por elemento: {Object.entries(rel.triadsPerElement).map(([k, v]) => `${k} ${v}`).join(' · ')}
          </li>
          <li>Frases &quot;Eu agradeço&quot;: {rel.agradecoSentences}</li>
          <li>Verbos no passado: {rel.agradecoPastTense.length}</li>
          <li>Palavras negativas: {rel.agradecoNegative.length}</li>
          <li>Terceira pessoa na Trindade: {rel.thirdPersonInTrindade.length}</li>
          <li>Travessões: {rel.dashesFound} · Palavras: {rel.words}</li>
          {rel.notes.length > 0 && (
            <li className="pt-2" style={{ color: cor }}>
              {rel.notes.join(' · ')}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default function AdminMapaCuraComparacao() {
  const [registros, setRegistros] = useState<Registro[]>([])
  const [selecionado, setSelecionado] = useState('')
  const [rodando, setRodando] = useState(false)
  const [erro, setErro] = useState('')
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [visao, setVisao] = useState<'score' | 'lado' | 'gemini' | 'claude'>('score')
  const [carregandoLista, setCarregandoLista] = useState(true)
  const [erroLista, setErroLista] = useState('')

  useEffect(() => {
    void carregar()
  }, [])

  async function carregar() {
    setCarregandoLista(true)
    try {
      const res = (await lumi.functions.invoke('adminMapaList', {
        method: 'POST',
        body: {},
      })) as { registros?: Registro[]; error?: string }
      if (res?.error) {
        setErroLista(`Não foi possível carregar as respostas: ${res.error}`)
      }
      setRegistros(res?.registros ?? [])
    } catch (e) {
      console.error('Erro ao carregar respostas:', e)
      setErroLista('Não foi possível carregar as respostas agora.')
    } finally {
      setCarregandoLista(false)
    }
  }

  async function comparar() {
    if (!selecionado) {
      setErro('Escolha uma pessoa para comparar.')
      return
    }
    setErro('')
    setResultado(null)
    setRodando(true)

    async function pedir(model: 'gemini' | 'claude'): Promise<RespostaModelo> {
      try {
        const res = (await lumi.functions.invoke('generateMapaCura', {
          method: 'POST',
          body: { compare: true, recordId: selecionado, model },
        })) as RespostaModelo
        return res ?? {}
      } catch (e) {
        const detalhe = e instanceof Error ? e.message : typeof e === 'string' ? e : ''
        return {
          text: '',
          relatorio: null,
          error:
            detalhe && !/failed to fetch/i.test(detalhe)
              ? detalhe
              : 'A geração demorou mais do que o tempo disponível. Tente novamente.',
        }
      }
    }

    const [g, c] = await Promise.all([pedir('gemini'), pedir('claude')])

    const geminiText = (g.text || '').trim()
    const claudeText = (c.text || '').trim()
    const erroGemini = g.error || (geminiText ? '' : 'O Gemini não retornou resultado.')
    const erroClaude = c.error || (claudeText ? '' : 'O Claude não retornou resultado.')

    if (!geminiText && !claudeText) {
      setErro(erroClaude || erroGemini || 'Não foi possível concluir a comparação agora.')
    } else {
      setResultado({
        name: g.name || c.name || '',
        model: c.model || 'claude-sonnet-4-5',
        gemini: geminiText,
        claude: claudeText,
        relatorioGemini: g.relatorio ?? null,
        relatorioClaude: c.relatorio ?? null,
        erroGemini,
        erroClaude,
      })
      setVisao('score')
    }

    setRodando(false)
    return
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-5" style={{ borderColor: 'rgba(224,184,112,0.3)' }}>
        <h3 className="font-display text-xl mb-2" style={{ color: 'var(--ink,#ece4d1)' }}>
          Comparar modelos
        </h3>
        <p className="font-body text-sm mb-4" style={{ color: 'var(--ink-soft,#a89f8c)' }}>
          Gera o mesmo mapa com as mesmas respostas usando Gemini e Claude, e aponta automaticamente onde cada um
          desrespeitou as regras do prompt. A comparação não altera o mapa da compradora.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selecionado}
            onChange={e => setSelecionado(e.target.value)}
            className="input-field flex-1 font-body"
            aria-label="Escolher pessoa"
            style={{
              backgroundColor: '#121a22',
              color: '#ece4d1',
              colorScheme: 'dark',
            }}
          >
            <option
              value=""
              style={{ backgroundColor: '#121a22', color: '#ece4d1' }}
            >
              {carregandoLista
                ? 'Carregando respostas...'
                : registros.length === 0
                  ? 'Nenhuma resposta encontrada'
                  : 'Escolha uma pessoa...'}
            </option>
            {registros.map(r => (
              <option
                key={r._id}
                value={r._id}
                style={{ backgroundColor: '#121a22', color: '#ece4d1' }}
              >
                {r.name || 'Sem nome'} — {r.email}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={comparar}
            disabled={rodando}
            className="btn-primary px-6 py-3 font-body disabled:opacity-60"
          >
            {rodando ? 'Gerando os dois...' : 'Comparar'}
          </button>
        </div>
        {erroLista && (
          <p className="font-body text-sm mt-3" style={{ color: '#E07B3C' }} role="alert">
            {erroLista}
          </p>
        )}
        {erro && (
          <p className="font-body text-sm mt-3" style={{ color: '#E07B3C' }} role="alert">
            {erro}
          </p>
        )}
        <p className="font-body text-[12px] mt-3" style={{ color: 'rgba(236,228,209,0.45)' }}>
          Leva cerca de 30 a 60 segundos, porque as duas gerações rodam ao mesmo tempo.
        </p>
      </div>

      {resultado && (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <ScoreCard titulo="Gemini 2.5 Flash" rel={resultado.relatorioGemini} erro={resultado.erroGemini} />
            <ScoreCard
              titulo={`Claude (${resultado.model})`}
              rel={resultado.relatorioClaude}
              erro={resultado.erroClaude}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {(
              [
                ['score', 'Resumo'],
                ['lado', 'Lado a lado'],
                ['gemini', 'Só Gemini'],
                ['claude', 'Só Claude'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setVisao(k)}
                className="px-4 py-2 rounded font-body text-sm"
                style={{
                  background: visao === k ? 'rgba(224,184,112,0.16)' : 'transparent',
                  border: `1px solid ${visao === k ? GOLD_SOFT : 'rgba(236,228,209,0.2)'}`,
                  color: visao === k ? GOLD_SOFT : 'var(--ink-soft,#a89f8c)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {visao === 'lado' && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <p className="font-body text-sm mb-3" style={{ color: GOLD_SOFT }}>
                  Gemini
                </p>
                {resultado.gemini ? <MapaCuraResult text={resultado.gemini} /> : <p style={{ color: '#E07B3C' }}>Sem resultado.</p>}
              </div>
              <div>
                <p className="font-body text-sm mb-3" style={{ color: GOLD_SOFT }}>
                  Claude
                </p>
                {resultado.claude ? <MapaCuraResult text={resultado.claude} /> : <p style={{ color: '#E07B3C' }}>Sem resultado.</p>}
              </div>
            </div>
          )}

          {visao === 'gemini' &&
            (resultado.gemini ? <MapaCuraResult text={resultado.gemini} /> : <p style={{ color: '#E07B3C' }}>Sem resultado.</p>)}

          {visao === 'claude' &&
            (resultado.claude ? <MapaCuraResult text={resultado.claude} /> : <p style={{ color: '#E07B3C' }}>Sem resultado.</p>)}
        </>
      )}
    </div>
  )
}
